import { useState, useEffect } from 'react';
import { useBatchApi } from './useBatchApi';
import { useMsalAuth } from './useMsalAuth';
import { createCancelJobService } from '../services/cancel_job';
import { getBatchBaseUrl } from '../config/trapi';
import type { BatchEnvironment } from '../config/trapi';

interface BatchWorkflowState {
  isProcessing: boolean;
  message: string;
  currentStep: string;
  jobId: string;
  jobStatus: string;
  fileId: string;
}

const STORAGE_KEY = 'batch-workflow-state';

function loadPersistedState(): BatchWorkflowState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Only restore job ID if there was an active job, but clear messages and steps for clean start
      return { 
        isProcessing: false,  // Never restore processing state
        message: '',          // Always start with clean message
        currentStep: '',      // Always start with clean step
        jobId: parsed.jobId || '',      // Restore job ID if it exists
        jobStatus: parsed.jobStatus || '',  // Restore status if it exists
        fileId: parsed.fileId || ''     // Restore file ID if it exists
      };
    }
  } catch (error) {
    console.warn('Failed to load persisted batch workflow state:', error);
  }
  
  return {
    isProcessing: false,
    message: '',
    currentStep: '',
    jobId: '',
    jobStatus: '',
    fileId: ''
  };
}

function saveStateToStorage(state: BatchWorkflowState) {
  try {
    // Only persist essential job state, not transient messages/steps
    const persistedState = {
      isProcessing: false, // Never persist processing state
      message: '',         // Never persist messages - they should be fresh
      currentStep: '',     // Never persist steps - they should be fresh
      jobId: state.jobId,
      jobStatus: state.jobStatus,
      fileId: state.fileId
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedState));
  } catch (error) {
    console.warn('Failed to save batch workflow state:', error);
  }
}

export function useBatchWorkflow() {
  const [state, setState] = useState<BatchWorkflowState>(loadPersistedState);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    saveStateToStorage(state);
  }, [state]);

  const batchApi = useBatchApi();
  const { getToken, getUserOid } = useMsalAuth();

  const updateState = (updates: Partial<BatchWorkflowState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const runBatchWorkflow = async (
    file: File,
    selectedApiPath: string,
    selectedApiVersion: string,
    selectedEnvironment: BatchEnvironment
  ) => {
    const config = { selectedApiPath, selectedApiVersion, selectedEnvironment };

    updateState({
      isProcessing: true,
      message: 'Starting batch workflow...',
      currentStep: '',
      jobId: '',
      jobStatus: '',
      fileId: ''
    });

    try {
      // Step 1: Upload file
      updateState({ 
        currentStep: 'Uploading file...',
        message: `STEP 1: Uploading file...\nProcessing: ${file.name}` 
      });
      
      const uploadedFileId = await batchApi.uploadFile(file, config);
      
      updateState({ 
        fileId: uploadedFileId,
        message: `File uploaded successfully!` 
      });

      // Step 2: Wait for file processing
      updateState({ 
        currentStep: 'Waiting for file processing...',
        message: 'Waiting for file processing to complete...' 
      });
      
      await batchApi.waitForFileProcessing(uploadedFileId, config, (status) => {
        updateState({ message: `Polling file status... (current: ${status})` });
      });
      
      updateState({ message: 'File processing succeeded.' });

      // Step 3: Submit batch job
      updateState({ 
        currentStep: 'Submitting batch job...',
        message: 'STEP 2: Submitting batch job...' 
      });
      
      const batchJobId = await batchApi.submitBatchJob(uploadedFileId, config);
      
      updateState({ 
        jobId: batchJobId,
        message: `Batch job submitted successfully!` 
      });

      // Step 4: Wait for job completion
      updateState({ 
        currentStep: 'Waiting for job completion...' 
      });
      
      const finalStatus = await batchApi.waitForJobCompletion(
        batchJobId, 
        config, 
        (status) => {
          updateState({ 
            message: `Polling job status... (current: ${status})`,
            jobStatus: status 
          });
        }
      );
      
      updateState({ 
        jobStatus: finalStatus,
        message: `Job completed with status: ${finalStatus}` 
      });

      // Step 5: Get results
      updateState({ 
        currentStep: 'Downloading results...',
        message: 'Getting job results...' 
      });
      
      const downloadedFileName = await batchApi.getJobResults(batchJobId, config, file.name);

      updateState({
        currentStep: finalStatus.includes('partially') ? 'Batch Job Partially Completed' : 'Batch Job Submitted Completed',
        message: finalStatus.includes('partially')
          ? `Batch job completed with partial failures.\nSuccessful results downloaded: ${downloadedFileName}\nDownload the error JSONL from Recent Batch Jobs for failed rows.`
          : `Batch job completed successfully!\nResults downloaded: ${downloadedFileName}`
      });

    } catch (error) {
      console.error('Batch workflow error:', error);
      
      let errorMessage = 'An unknown error occurred.';
      let troubleshootingTips = '';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Provide specific troubleshooting tips based on error type
        if (errorMessage.includes('stuck in queue') || errorMessage.includes('stuck in status')) {
          troubleshootingTips = '\n\nThe job may be experiencing high server load. Try:\n• Using the Refresh button to start over\n• Submitting during off-peak hours\n• Checking if your .jsonl file format is correct';
        } else if (errorMessage.includes('File upload failed')) {
          troubleshootingTips = '\n\nFile upload issue. Check:\n• File is a valid .jsonl format\n• File size is reasonable\n• Network connection is stable';
        } else if (errorMessage.includes('Job submission failed')) {
          troubleshootingTips = '\n\nJob submission issue. Verify:\n• API path and version are correct\n• Your authentication is valid\n• File was uploaded successfully';
        } else if (errorMessage.includes('authentication') || errorMessage.includes('unauthorized')) {
          troubleshootingTips = '\n\nAuthentication issue. Try:\n• Refreshing the page to re-authenticate\n• Checking your Azure permissions';
        }
      }
      
      updateState({
        message: `Error: ${errorMessage}${troubleshootingTips}`,
        currentStep: 'Failed'
      });
    } finally {
      updateState({ isProcessing: false });
    }
  };

  const cancelCurrentJob = async (
    selectedApiPath: string,
    selectedApiVersion: string,
    selectedEnvironment: BatchEnvironment
  ) => {
    if (!state.jobId) return;

    try {
      const cancelJobService = createCancelJobService(
        getBatchBaseUrl(selectedEnvironment),
        getToken,
        getUserOid
      );
      const result = await cancelJobService.cancelJob(state.jobId, { selectedApiPath, selectedApiVersion });
      
      if (result.success) {
        updateState({
          message: 'Job cancellation requested.',
          isProcessing: false,
          currentStep: 'Cancelled',
          jobId: '',
          fileId: ''
        });
      } else {
        updateState({
          message: `Cancel failed: ${result.message}`
        });
      }
    } catch (error) {
      updateState({
        message: `Cancel failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  };

  const resetWorkflow = () => {
    const resetState = {
      isProcessing: false,
      message: '',
      currentStep: '',
      jobId: '',
      jobStatus: '',
      fileId: ''
    };
    setState(resetState);
    // Clear persisted state
    localStorage.removeItem(STORAGE_KEY);
  };

  return {
    ...state,
    runBatchWorkflow,
    cancelCurrentJob,
    resetWorkflow
  };
}
